/**
 * ArtworkStep — Step 1: Dimensions (W×H) + depth + size presets.
 */

import { T } from '@/styles/tokens';
import { StepNextButton } from './StepNextButton';
import { SectionIcon } from './SectionIcons';
import type { DepthValue } from './DimensionInput';

const PRESETS = [
  { w: 8, h: 10, label: '8×10' },
  { w: 11, h: 14, label: '11×14' },
  { w: 16, h: 20, label: '16×20' },
  { w: 24, h: 36, label: '24×36' },
];

const DEPTHS: DepthValue[] = ['0.75', '1.5', '2+'];

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: T.charcoal400,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 8,
  fontFamily: T.fontFamily,
};

const cardStyle: React.CSSProperties = {
  background: T.white,
  borderRadius: 16,
  border: `1px solid ${T.cream200}`,
  padding: 24,
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};

interface ArtworkStepProps {
  width: number;
  height: number;
  depth: DepthValue;
  onChangeWidth: (w: number) => void;
  onChangeHeight: (h: number) => void;
  onChangeDepth: (d: DepthValue) => void;
  onNext: () => void;
}

export function ArtworkStep({ width, height, depth, onChangeWidth, onChangeHeight, onChangeDepth, onNext }: ArtworkStepProps) {
  return (
    <div>
      <SectionIcon icon="artwork" label="Artwork" />
      <div style={cardStyle}>
        {/* Dimensions */}
        <div style={{ marginBottom: 24 }}>
          <div style={fieldLabelStyle}>Dimensions</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 10, alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                inputMode="numeric"
                value={width || ''}
                onChange={(e) => onChangeWidth(parseInt(e.target.value) || 0)}
                placeholder="16"
                style={{
                  width: '100%',
                  height: 48,
                  padding: '0 36px 0 14px',
                  fontSize: 16,
                  fontWeight: 700,
                  fontFamily: T.fontFamily,
                  color: T.text,
                  background: T.white,
                  border: `1.5px solid ${T.charcoal200}`,
                  borderRadius: 12,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, fontWeight: 500, color: T.charcoal300, pointerEvents: 'none' }}>in</span>
            </div>
            <span style={{ color: T.charcoal300, fontSize: 14, fontWeight: 500, lineHeight: '48px' }}>×</span>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                inputMode="numeric"
                value={height || ''}
                onChange={(e) => onChangeHeight(parseInt(e.target.value) || 0)}
                placeholder="20"
                style={{
                  width: '100%',
                  height: 48,
                  padding: '0 36px 0 14px',
                  fontSize: 16,
                  fontWeight: 700,
                  fontFamily: T.fontFamily,
                  color: T.text,
                  background: T.white,
                  border: `1.5px solid ${T.charcoal200}`,
                  borderRadius: 12,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, fontWeight: 500, color: T.charcoal300, pointerEvents: 'none' }}>in</span>
            </div>
          </div>
          {/* Presets */}
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            {PRESETS.map((p) => {
              const sel = p.w === width && p.h === height;
              return (
                <button
                  key={p.label}
                  onClick={() => { onChangeWidth(p.w); onChangeHeight(p.h); }}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 999,
                    border: sel ? `1.5px solid ${T.primary}` : `1px solid ${T.charcoal200}`,
                    background: sel ? T.tealSoft : T.cream100,
                    fontSize: 12,
                    fontWeight: sel ? 600 : 500,
                    color: sel ? T.primaryDark : T.charcoal600,
                    fontFamily: T.fontFamily,
                    cursor: 'pointer',
                  }}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Depth */}
        <div>
          <div style={fieldLabelStyle}>Depth</div>
          <div style={{
            display: 'flex',
            borderRadius: 10,
            border: `1.5px solid ${T.charcoal200}`,
            overflow: 'hidden',
            background: T.cream100,
          }}>
            {DEPTHS.map((d, i) => {
              const sel = d === depth;
              return (
                <button
                  key={d}
                  onClick={() => onChangeDepth(d)}
                  style={{
                    flex: 1,
                    height: 40,
                    border: 'none',
                    borderLeft: i > 0 ? `1px solid ${T.charcoal200}` : 'none',
                    background: sel ? T.white : 'transparent',
                    color: sel ? T.text : T.charcoal400,
                    fontSize: 13,
                    fontWeight: sel ? 600 : 500,
                    fontFamily: T.fontFamily,
                    cursor: 'pointer',
                    boxShadow: sel ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                  }}
                >
                  {d}&quot;
                </button>
              );
            })}
          </div>
        </div>

        <StepNextButton label="Next" disabled={!width || !height} onPress={onNext} />
      </div>
    </div>
  );
}
